import React from "react";
import { Plus, FileText, X } from "@semo/icons";
import type { QuoteAttachmentDto } from "@semo/api-contracts";
import { quoteToast } from "../../lib/toast-utils";
import { QuoteErrorType, QuoteErrorDetail } from "../../lib/error-types";
import { Textarea, Input, cn } from "@semo/shared-ui";

// Re-export for backwards compatibility
export type { QuoteAttachmentDto };

export interface QuoteUIFile {
  id?: string;
  fileName: string;
  fileSize: string | number;
  url?: string;
  file?: File;
}

interface QuoteFooterProps {
  notes?: string;
  files: QuoteUIFile[];
  onAddFile?: () => void;
  onRemoveFile?: (id: string) => void;
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  readOnly?: boolean;
  onNotesChange?: (notes: string) => void;
  uploadError?: QuoteErrorDetail | null;
}

export function QuoteFooter({
  notes = "• 본 견적서의 유효기간은 발행일로부터 30일입니다.\n• 납기일은 발주 후 2주 이내입니다.\n• 결제조건: 계약 시 50%, 납품 완료 후 50% 현금 결제 조건입니다.",
  files,
  onAddFile,
  onRemoveFile,
  onFileChange,
  fileInputRef,
  readOnly = false,
  onNotesChange,
  uploadError,
}: QuoteFooterProps) {
  const handleDownloadFile = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();
      const contentType =
        response.headers.get("content-type") || "application/octet-stream";
      const blob = new Blob([arrayBuffer], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download file:", error);
      quoteToast.error({
        type: QuoteErrorType.UNKNOWN,
        message: "파일 다운로드 실패",
        action: "다시 시도해주세요.",
      });
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mt-auto">
      <div>
        <div className="font-semibold text-gray-800 mb-2 text-xs sm:text-sm md:text-sm flex items-center gap-2">
          <span className="w-1 h-3 sm:h-4 bg-primary rounded-sm block"></span>
          특이사항
        </div>
        {!readOnly ? (
          <Textarea
            value={notes}
            onChange={(e) => onNotesChange?.(e.target.value)}
            placeholder={`특이사항을 입력해주세요.

예시:
• 본 견적서의 유효기간은 발행일로부터 30일입니다.
• 납기일은 발주 후 2주 이내입니다.
• 결제조건: 계약 시 50%, 납품 완료 후 50% 현금 결제 조건입니다.`}
            className="w-full h-[200px] text-2xs sm:text-2xs md:text-xs text-gray-700 leading-relaxed resize-none bg-[#f8fafc]/50 border-[1.2px] border-slate-200 hover:border-primary/40 hover:bg-white focus:bg-white focus:border-primary/60 focus:ring-4 focus:ring-primary/10 rounded-xl p-3 sm:p-4 transition-all placeholder:text-gray-400"
          />
        ) : (
          <div className="bg-[#f9fafb] border border-[#eef2f7] rounded-lg p-3 sm:p-4 h-[200px] text-2xs sm:text-2xs md:text-xs text-gray-600 leading-relaxed overflow-y-auto whitespace-pre-wrap">
            {notes}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="font-semibold text-gray-800 text-xs sm:text-sm md:text-sm flex items-center gap-2 shrink-0 whitespace-nowrap">
            <span className="w-1 h-3 sm:h-4 bg-primary rounded-sm block"></span>
            첨부파일
          </div>
          <Input
            type="file"
            ref={fileInputRef}
            onChange={onFileChange}
            className="hidden"
          />
        </div>
        <div
          className={cn(
            "rounded-lg p-3 sm:p-4 h-[200px] overflow-y-auto transition-all",
            !readOnly
              ? "bg-slate-50 border-2 border-dashed border-slate-200 hover:border-primary/40 hover:bg-primary/5 cursor-pointer"
              : "bg-[#f9fafb] border border-[#eef2f7]",
          )}
          onDragOver={(e) => {
            if (readOnly) return;
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            if (readOnly || !onFileChange) return;
            e.preventDefault();
            e.stopPropagation();
            const droppedFiles = Array.from(e.dataTransfer.files);
            if (droppedFiles.length > 0) {
              // Create a mock event to re-use handleFileChange logic
              const mockEvent = {
                target: { files: e.dataTransfer.files },
              } as unknown as React.ChangeEvent<HTMLInputElement>;
              onFileChange(mockEvent);
            }
          }}
          onClick={() => !readOnly && onAddFile?.()}
        >
          {uploadError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <span className="text-red-600 text-sm">⚠️</span>
                <div className="flex-1">
                  <p className="text-red-700 text-xs font-medium">
                    {uploadError.message}
                  </p>
                  {uploadError.action && (
                    <p className="text-red-600 text-2xs mt-1">
                      {uploadError.action}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          {files.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 py-4 gap-2">
              <Plus className="w-8 h-8 opacity-20" />
              <div className="flex flex-col items-center gap-1">
                <span className="text-2xs sm:text-xs font-semibold text-gray-500">
                  파일을 마우스로 끌어오거나 클릭하여 추가
                </span>
                <span className="text-2xs sm:text-2xs text-gray-400">
                  (최대 5개, 개당 10MB 이하)
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5 sm:space-y-2">
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`group flex items-center justify-between text-2xs sm:text-2xs md:text-xs text-gray-600 ${
                    readOnly && file.url
                      ? "hover:text-primary-text cursor-pointer transition-colors"
                      : !readOnly
                        ? "bg-white border border-gray-100 rounded px-2 py-1.5 hover:border-primary/30"
                        : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (readOnly && file.url) {
                      handleDownloadFile(file.url, file.fileName);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 text-gray-400" />
                    <span className="truncate max-w-[150px] sm:max-w-[200px] font-medium">
                      {file.fileName}
                    </span>
                    {!readOnly && (
                      <span className="text-gray-400 text-2xs sm:text-2xs shrink-0">
                        ({file.fileSize})
                      </span>
                    )}
                  </div>
                  {!readOnly && onRemoveFile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        file.id && onRemoveFile(file.id);
                      }}
                      className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
